import { assertGrnLineCondition, DomainError, parsePurchaseOrderDelivery } from '@felix/domain';
import type { StatusTone } from '../../StatusBadge.js';
import { type NumberValue, numberOf } from './requisitions.js';
import type {
  GoodsReceiptForm,
  GoodsReceiptInput,
  GoodsReceiptStatus,
  GoodsReceiptSummary,
  GrnCondition,
  GrnLineStatus,
  PurchaseOrderDeliveryInput,
  PurchaseOrderDetail,
  PurchaseOrderEvent,
  PurchaseOrderStatus,
  PurchaseOrderSummary,
} from './types.js';

/**
 * Sipariş (PO) ve mal kabulü (GRN) ekranlarının saf hesapları (M12c Task 16–17; ADR 0040): yol,
 * liste süzgeci, teslim bilgileri formu ve GRN formu (gemi ve bulutla aynı kurallar;
 * `@felix/domain`).
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const blank = (value: string) => (value.trim() === '' ? null : value.trim());

/* ---------- Sipariş ---------- */

export const PURCHASE_ORDERS_PATH = '/purchasing/orders';
export const PO_TABS = ['items', 'delivery', 'receipts', 'history'] as const;
export type PoTab = (typeof PO_TABS)[number];
export type PurchaseOrderRoute = { view: 'list' } | { view: 'detail'; id: string; tab: PoTab };

/** Ekranın görünümü sorgu dizesinden (`?id=…`, `?id=…&tab=receipts`). */
export function purchaseOrderRoute(search: string): PurchaseOrderRoute {
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (!id || !UUID.test(id)) return { view: 'list' };
  const tab = params.get('tab');
  return {
    view: 'detail',
    id,
    tab: (PO_TABS as readonly string[]).includes(tab ?? '') ? (tab as PoTab) : 'items',
  };
}

export function purchaseOrderPath(route: PurchaseOrderRoute): string {
  if (route.view === 'list') return PURCHASE_ORDERS_PATH;
  return route.tab === 'items'
    ? `${PURCHASE_ORDERS_PATH}?id=${route.id}`
    : `${PURCHASE_ORDERS_PATH}?id=${route.id}&tab=${route.tab}`;
}

/**
 * PO durumunun rozeti (`satin-alma-akisi.jpeg` "Issued" yeşil; `gemi-satin-alma.jpeg` "Partially
 * Received" turuncu, "Completed" yeşil, "Cancelled" kırmızı).
 */
export const PO_TONE: Record<PurchaseOrderStatus, StatusTone> = {
  draft: 'neutral',
  pending_approval: 'warning',
  issued: 'success',
  sent: 'info',
  confirmed: 'info',
  partially_delivered: 'warning',
  delivered: 'success',
  completed: 'success',
  cancelled: 'danger',
};

export const PO_STATUSES: PurchaseOrderStatus[] = [
  'draft',
  'pending_approval',
  'issued',
  'sent',
  'confirmed',
  'partially_delivered',
  'delivered',
  'completed',
  'cancelled',
];

export interface PurchaseOrderFilter {
  search: string;
  vesselId: string | null;
  status: PurchaseOrderStatus | null;
  /** Sipariş günü aralığı (`YYYY-MM-DD`, ikisi de dahil). */
  from: string | null;
  to: string | null;
}

export const PO_NO_FILTER: PurchaseOrderFilter = {
  search: '',
  vesselId: null,
  status: null,
  from: null,
  to: null,
};

const matches = (text: string, query: string) =>
  text.toLocaleLowerCase('en').includes(query.trim().toLocaleLowerCase('en'));

export function filterPurchaseOrders(
  items: readonly PurchaseOrderSummary[],
  filter: PurchaseOrderFilter,
): PurchaseOrderSummary[] {
  return items.filter(
    (item) =>
      (!filter.search.trim() ||
        matches(item.poNo, filter.search) ||
        matches(item.supplierName, filter.search) ||
        matches(item.requisitionNo, filter.search)) &&
      (!filter.vesselId || item.vesselId === filter.vesselId) &&
      (!filter.status || item.status === filter.status) &&
      (!filter.from || item.orderDate >= filter.from) &&
      (!filter.to || item.orderDate <= filter.to),
  );
}

export interface DeliveryFormValues {
  expectedDelivery: string | null;
  deliveryLocation: string;
  /** Teslim limanının kodu (ADR 0048 §3); serbest metinde null. */
  deliveryPortCode: string | null;
  deliveryTerms: string;
  paymentTerms: string;
  warranty: string;
  remarks: string;
}

export function deliveryForm(
  detail: Pick<
    PurchaseOrderDetail,
    | 'expectedDelivery'
    | 'deliveryLocation'
    | 'deliveryPortCode'
    | 'deliveryTerms'
    | 'paymentTerms'
    | 'warranty'
    | 'remarks'
  >,
): DeliveryFormValues {
  return {
    expectedDelivery: detail.expectedDelivery,
    deliveryLocation: detail.deliveryLocation ?? '',
    deliveryPortCode: detail.deliveryPortCode ?? null,
    deliveryTerms: detail.deliveryTerms ?? '',
    paymentTerms: detail.paymentTerms ?? '',
    warranty: detail.warranty ?? '',
    remarks: detail.remarks ?? '',
  };
}

export function toDeliveryInput(values: DeliveryFormValues): PurchaseOrderDeliveryInput {
  return {
    expectedDelivery: values.expectedDelivery,
    deliveryLocation: blank(values.deliveryLocation),
    deliveryPortCode: blank(values.deliveryLocation) === null ? null : values.deliveryPortCode,
    deliveryTerms: blank(values.deliveryTerms),
    paymentTerms: blank(values.paymentTerms),
    warranty: blank(values.warranty),
    remarks: blank(values.remarks),
  };
}

/** Alan hataları (alan → kod); boşsa geçerli. Şema gemi ve bulutla aynı. */
export function validateDeliveryForm(values: DeliveryFormValues): Record<string, string> {
  const parsed = parsePurchaseOrderDelivery(toDeliveryInput(values));
  if (parsed.ok) return {};
  return Object.fromEntries(parsed.issues.map((issue) => [issue.path, issue.code]));
}

/**
 * "Send to Supplier" düğmesinin hali (ADR 0040 #181, M12c-R3): kuyrukta ya da gönderilmekte olan
 * e-posta varsa düğme yerine not (`pending`); e-posta ayarı yoksa düğme yok ("Mark as Sent").
 */
export function sendState(
  detail: Pick<PurchaseOrderDetail, 'can' | 'mailEnabled' | 'emailPending'>,
): 'send' | 'pending' | 'none' {
  if (detail.emailPending) return 'pending';
  return detail.can.send && detail.mailEnabled !== false ? 'send' : 'none';
}

/**
 * Senkrondan sonra ofisin kararıyla çelişen gemi kaydı (ADR 0040 #186, M12c-R10): iptal edilmiş
 * PO'ya gelen kabul ve kapanmış PO'ya gelen ya da onda iptal edilen GRN. Kayıt kalır; kartta ve
 * listede uyarılır.
 */
export function orderWarnings(
  order: Pick<PurchaseOrderSummary, 'receivedAfterCancel'> & { receivedAfterClose?: number },
): ('receivedAfterCancel' | 'receivedAfterClose')[] {
  const warnings: ('receivedAfterCancel' | 'receivedAfterClose')[] = [];
  if (order.receivedAfterCancel) warnings.push('receivedAfterCancel');
  if ((order.receivedAfterClose ?? 0) > 0) warnings.push('receivedAfterClose');
  return warnings;
}

/**
 * Gönderim penceresinde onay verilebilir mi (M12c-R28): tedarikçinin e-postası yoksa istek
 * `SUPPLIER_EMAIL_MISSING` ile dönecektir; onay kapalı, pencere başka yolu önerir.
 */
export function sendConfirmable(detail: Pick<PurchaseOrderDetail, 'supplier'>): boolean {
  return Boolean(detail.supplier.email?.trim());
}

/**
 * "Mark as Sent" önerilir mi (ADR 0040 #181, M12c-R28): e-posta ayarı yoksa ya da tedarikçinin
 * e-postası yoksa PO yazdırılır ya da indirilir, elle gönderilir ve işaretlenir.
 */
export function markSentOffered(
  detail: Pick<PurchaseOrderDetail, 'can' | 'mailEnabled' | 'supplier'>,
): boolean {
  return Boolean(detail.can.markSent) && (detail.mailEnabled === false || !sendConfirmable(detail));
}

/**
 * Geçmiş olayının alt satırı (M12c-R18): kişi, çevrilmiş gönderim yolu ve gerekçe. Ham değer
 * ("email"/"manual") gösterilmez.
 */
export function historyDetail(
  event: Pick<PurchaseOrderEvent, 'byName' | 'reason' | 'via'>,
  t: (key: string) => string,
): string {
  const via = event.via ? t(`purchasing.po.detail.sentVia.${event.via}`) : null;
  return [event.byName, via, event.reason].filter(Boolean).join(' · ') || '—';
}

/* ---------- Mal kabulü ---------- */

/**
 * GRN satırının stok bilgisi (ADR 0040 #185, M12c-R17): kabul edilen katalog kalemi stoğa girer;
 * kabulü sıfır katalog kaleminden stoğa bir şey girmez; katalog dışı kalem stoğa hiç girmez.
 */
export function receiptLineBooking(line: {
  accepted: number;
  stockItemId: string | null;
  materialId?: string | null;
  substitute?: boolean;
}): 'stocked' | 'nothingBooked' | 'notStocked' | 'substitute' {
  // İkame satırı stoğa girmez (ADR 0115 §4)
  if (line.substitute === true) return 'substitute';
  const catalogue = line.materialId != null || line.stockItemId !== null;
  if (!catalogue) return 'notStocked';
  return line.accepted > 0 && line.stockItemId !== null ? 'stocked' : 'nothingBooked';
}

export const GRN_PATH = '/purchasing/grn';

export type GrnRoute =
  | { view: 'list' }
  | { view: 'new'; orderId: string | null }
  | { view: 'detail'; id: string };

/** Ekranın görünümü sorgu dizesinden (`?new=1&po=…`, `?id=…`). */
export function grnRoute(search: string): GrnRoute {
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (id && UUID.test(id)) return { view: 'detail', id };
  if (params.get('new') !== '1') return { view: 'list' };
  const po = params.get('po');
  return { view: 'new', orderId: po && UUID.test(po) ? po : null };
}

export function grnPath(route: GrnRoute): string {
  switch (route.view) {
    case 'list':
      return GRN_PATH;
    case 'new':
      return route.orderId ? `${GRN_PATH}?new=1&po=${route.orderId}` : `${GRN_PATH}?new=1`;
    case 'detail':
      return `${GRN_PATH}?id=${route.id}`;
  }
}

/** GRN durumu: tasarımda "Completed" yeşil; listede "Partial" turuncu. */
export const GRN_TONE: Record<GoodsReceiptStatus, StatusTone> = {
  completed: 'success',
  partial: 'warning',
  cancelled: 'neutral',
};

/** Satır durumu: tasarımda "OK" yeşil. */
export const GRN_LINE_TONE: Record<GrnLineStatus, StatusTone> = {
  ok: 'success',
  partial: 'warning',
  issue: 'danger',
};

export const GRN_STATUSES: GoodsReceiptStatus[] = ['completed', 'partial', 'cancelled'];
export const GRN_CONDITIONS: GrnCondition[] = [
  'accepted',
  'accepted_with_remarks',
  'rejected',
  'damaged',
  'wrong_item',
  'short_delivery',
  'certificate_missing',
  'specification_not_met',
];

export interface GrnFilter {
  search: string;
  vesselId: string | null;
  status: GoodsReceiptStatus | null;
  from: string | null;
  to: string | null;
}

export const GRN_NO_FILTER: GrnFilter = {
  search: '',
  vesselId: null,
  status: null,
  from: null,
  to: null,
};

export function filterGoodsReceipts(
  items: readonly GoodsReceiptSummary[],
  filter: GrnFilter,
): GoodsReceiptSummary[] {
  return items.filter(
    (item) =>
      (!filter.search.trim() ||
        matches(item.grnNo, filter.search) ||
        matches(item.poNo, filter.search) ||
        matches(item.supplierName, filter.search)) &&
      (!filter.vesselId || item.vesselId === filter.vesselId) &&
      (!filter.status || item.status === filter.status) &&
      (!filter.from || item.receivedDate >= filter.from) &&
      (!filter.to || item.receivedDate <= filter.to),
  );
}

export interface GrnFormLine {
  purchaseOrderLineId: string;
  lineNo: number;
  name: string;
  unit: string;
  ordered: number;
  open: number;
  accepted: NumberValue;
  damaged: NumberValue;
  missing: NumberValue;
  condition: GrnCondition;
  batch: string;
  expiry: string | null;
  /** Kabul stoğa girer mi; ikame satırı girmez (ADR 0115 §4). */
  toStock: boolean;
  substitute: boolean;
  substituteFor: string | null;
}

export interface GrnFormValues {
  orderId: string | null;
  receivedDate: string;
  deliveryNote: string;
  receivedBy: string;
  remarks: string;
  lines: GrnFormLine[];
}

/** Seçilen siparişin açık kalemleri; varsayılan tam teslim (kabul = açık miktar). */
function linesOf(form: GoodsReceiptForm, orderId: string | null): GrnFormLine[] {
  const order = form.orders.find((o) => o.id === orderId);
  if (!order) return [];
  return order.lines
    .filter((line) => line.open > 0)
    .map((line) => ({
      purchaseOrderLineId: line.id,
      lineNo: line.lineNo,
      name: line.name,
      unit: line.unit,
      ordered: line.ordered,
      open: line.open,
      accepted: line.open,
      damaged: 0,
      missing: 0,
      condition: 'accepted',
      batch: '',
      expiry: null,
      toStock: line.toStock ?? (line.materialId !== null && line.substitute !== true),
      substitute: line.substitute === true,
      substituteFor: line.substitute === true ? (line.substituteFor ?? null) : null,
    }));
}

export function grnFormDefaults(form: GoodsReceiptForm, orderId: string | null): GrnFormValues {
  const known = form.orders.some((o) => o.id === orderId) ? orderId : null;
  return {
    orderId: known,
    receivedDate: form.today,
    deliveryNote: '',
    receivedBy: form.receivers.some((r) => r.id === form.me)
      ? form.me
      : (form.receivers[0]?.id ?? form.me),
    remarks: '',
    lines: linesOf(form, known),
  };
}

/** Sipariş değişince kalemler yenilenir; başlık korunur. */
export function selectOrder(
  values: GrnFormValues,
  form: GoodsReceiptForm,
  orderId: string | null,
): GrnFormValues {
  return { ...values, orderId, lines: linesOf(form, orderId) };
}

const amount = (value: NumberValue) => {
  const parsed = numberOf(value);
  return parsed === '' ? 0 : parsed;
};

/**
 * Miktara göre kalite sonucu önerisi (ADR 0040 #184): varsayılan "Accepted" hasarlı miktarla
 * "Damaged", eksik miktarla "Short Delivery" olur; kullanıcının seçtiği başka sonuç miktarlarla
 * tutarlıysa korunur, değilse aynı öneriye döner.
 */
export function suggestCondition(line: GrnFormLine): GrnCondition {
  const quantities = {
    acceptedQty: amount(line.accepted),
    damagedQty: amount(line.damaged),
    missingQty: amount(line.missing),
  };
  if (line.condition === 'accepted') {
    if (quantities.damagedQty > 0) return 'damaged';
    if (quantities.missingQty > 0) return 'short_delivery';
    return 'accepted';
  }
  try {
    assertGrnLineCondition({ condition: line.condition, ...quantities });
    return line.condition;
  } catch {
    if (quantities.damagedQty > 0) return 'damaged';
    if (quantities.missingQty > 0) return 'short_delivery';
    return 'accepted';
  }
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

/**
 * Alan hataları (`lines.<i>.<alan>` → kod; `_form` GRN'nin bütünü). Kayıt katmanıyla aynı kurallar
 * (`checkReceiptLines`): miktar sıfır ya da artı; kabul + hasarlı + eksik açık miktarı aşmaz;
 * kalite sonucu tutarlı; en az bir miktar.
 */
export function validateGrnForm(values: GrnFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!values.orderId) {
    errors.orderId = 'REQUIRED';
    return errors;
  }
  if (!values.receivedDate) errors.receivedDate = 'REQUIRED';
  if (!values.receivedBy) errors.receivedBy = 'REQUIRED';
  let any = false;
  for (const [index, line] of values.lines.entries()) {
    const quantities = {
      accepted: numberOf(line.accepted),
      damaged: numberOf(line.damaged),
      missing: numberOf(line.missing),
    };
    let invalid = false;
    for (const [field, value] of Object.entries(quantities)) {
      if (value === '' || value < 0) {
        errors[`lines.${index}.${field}`] = 'INVALID_NUMBER';
        invalid = true;
      }
    }
    if (invalid) continue;
    const total = round(amount(line.accepted) + amount(line.damaged) + amount(line.missing));
    if (total > 0) any = true;
    if (total > round(line.open)) {
      errors[`lines.${index}.accepted`] = 'OVER_OPEN';
      continue;
    }
    try {
      assertGrnLineCondition({
        condition: line.condition,
        acceptedQty: amount(line.accepted),
        damagedQty: amount(line.damaged),
        missingQty: amount(line.missing),
      });
    } catch (error) {
      errors[`lines.${index}.condition`] =
        error instanceof DomainError ? error.code : 'CONDITION_MISMATCH';
    }
  }
  if (!any && Object.keys(errors).length === 0) errors._form = 'EMPTY_RECEIPT';
  return errors;
}

export function toGrnInput(values: GrnFormValues): GoodsReceiptInput {
  return {
    purchaseOrderId: values.orderId ?? '',
    receivedDate: values.receivedDate,
    deliveryNote: blank(values.deliveryNote),
    receivedBy: values.receivedBy,
    remarks: blank(values.remarks),
    lines: values.lines.map((line) => ({
      purchaseOrderLineId: line.purchaseOrderLineId,
      acceptedQty: amount(line.accepted),
      damagedQty: amount(line.damaged),
      missingQty: amount(line.missing),
      condition: line.condition,
      serialNo: null,
      batchNo: blank(line.batch),
      expiryDate: line.expiry,
      remarks: null,
    })),
  };
}
